import { Coordinates } from 'src/interfaces/idashboard';

// Function to calculate bounds from polygons
export const calculateBoundsAll = (polygonPaths: Coordinates[][][]) => {
  if (!polygonPaths.length) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  polygonPaths.forEach(polygon => {
    polygon.forEach(path => {
      path.forEach(coord => {
        minLat = Math.min(minLat, coord.lat);
        maxLat = Math.max(maxLat, coord.lat);
        minLng = Math.min(minLng, coord.lng);
        maxLng = Math.max(maxLng, coord.lng);
      });
    });
  });

  return {
    north: maxLat,
    south: minLat,
    east: maxLng,
    west: minLng,
  };
};

// Function to calculate zoom level based on bounds
export const calculateZoomLevel = (bounds: any, mapWidth = 800, mapHeight = 500) => {
  if (!bounds) return 12;

  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI;
  const lngDiff = bounds.east - bounds.west;
  const lngFraction = lngDiff < 0 ? lngDiff + 360 : lngDiff;
  const lngFractionNormalized = lngFraction / 360;

  const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFractionNormalized);

  return Math.min(latZoom, lngZoom, ZOOM_MAX) - 1; // Subtract 1 for padding
};

export const getSubmarketCoordsById = (submarketId: string, submarkets: any[]) => {
  const submarket = [...submarkets].find(submarket => submarket.submarket_id == submarketId);
  return submarket ? { center: submarket.center, boundary: submarket.boundary } : null;
};

export const getSubmarketNameById = (
  submarketId: string,
  submarkets: any,
  extraFields: string[] | null = null
) => {
  if (!submarkets || !submarkets.length || !Array.isArray(submarkets)) return null;
  const submarket = [...submarkets].find(submarket => submarket.submarket_id == submarketId);

  if (extraFields) {
    const result = {
      submarket_name: submarket.submarket_name,
    };
    extraFields.forEach(field => {
      result[field] = submarket[field] || null;
    });
    return result;
  }

  return submarket ? submarket.submarket_name : null;
};

export const filterOutHiddenSubmarkets = (
  submarkets: any[],
  hiddenSubmarkets: string[],
  newlyAddedSubmarketId: string | null = null
) => {
  let filteredHiddenSubmarkets: any[] = [];

  if (hiddenSubmarkets.length) {
    filteredHiddenSubmarkets = submarkets.filter(
      submarket => !hiddenSubmarkets.includes(String(submarket.submarket_id))
    );
  } else {
    filteredHiddenSubmarkets = submarkets;
  }

  if (!newlyAddedSubmarketId) {
    return filteredHiddenSubmarkets;
  }

  let submarketsIncludingNewlyAdded: any[] = [];
  const newlyAdddSubmarketFromSubmarketList = filteredHiddenSubmarkets.find(
    submarket => submarket.submarket_id == newlyAddedSubmarketId
  );

  if (newlyAdddSubmarketFromSubmarketList) {
    submarketsIncludingNewlyAdded = [
      newlyAdddSubmarketFromSubmarketList,
      ...filteredHiddenSubmarkets.filter(
        submarket => submarket.submarket_id != newlyAddedSubmarketId
      ),
    ];
  } else {
    submarketsIncludingNewlyAdded = filteredHiddenSubmarkets;
  }

  return submarketsIncludingNewlyAdded;
};


export const getTop3Submarkets = (submarkets: any) => {
  let top3Submarkets: any[] = [];
  const adjustedSubmarkets: any = {};

  if (submarkets && submarkets !== 'loading' && Array.isArray(submarkets)) {
    top3Submarkets = [...submarkets].filter(submarket => submarket.neighborhood_grade).slice(0, 3);
  }

  top3Submarkets.forEach(submarket => {
    adjustedSubmarkets[submarket.submarket_id] = true;
  });

  if (top3Submarkets.length < 3) {
    let missingSubmarketsCount = 3 - top3Submarkets.length;
    top3Submarkets = [
      ...top3Submarkets,
      ...submarkets
        .filter(submarket => !adjustedSubmarkets[submarket.submarket_id])
        .slice(0, missingSubmarketsCount),
    ];
  }

  return top3Submarkets;
};

export const wrapLabel = (text: string, maxChars = 12): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    if (word.length > maxChars) {
      // flush the current line if it has content
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      // break long word into chunks
      const chunks = word.match(new RegExp(`.{1,${maxChars}}`, 'g')) || [];
      lines.push(...chunks);
    } else {
      // check if we can add this word to the current line
      if ((currentLine + ' ' + word).trim().length <= maxChars) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        lines.push(currentLine.trim());
        currentLine = word;
      }
    }
  });

  if (currentLine) {
    lines.push(currentLine.trim());
  }

  return lines;
};

export const checkIfMarketTrackedAlready = (
  eventType: 'submarkets_tab_viewed' | 'submarket_cta_viewed',
  marketID: number
) => {
  const localStorageKey = `tracked_${eventType}`;
  const trackedMarkets = localStorage.getItem(localStorageKey);
  let trackedMarketsArray: any = {};

  if (trackedMarkets) {
    trackedMarketsArray = JSON.parse(trackedMarkets);
    if (trackedMarketsArray[marketID]) return true;
  }

  trackedMarketsArray[marketID] = true;
  localStorage.setItem(localStorageKey, JSON.stringify(trackedMarketsArray));

  return false;
};
